import type {
  CustomerApplicationFieldSourceState,
  CustomerApplicationFieldValueContract,
  CustomerApplicationSourceLinkContract,
} from '@/contracts/customer-application-binding'
import { customerFieldTargetOptions } from '@/contracts/customers'
import { createSyntheticMeta } from '@/contracts/questionnaire'
import type {
  QuestionnaireSnapshotAnswer,
  QuestionnaireSnapshotField,
  QuestionnaireSnapshotFormatOption,
  QuestionnaireSnapshotMetadata,
  QuestionnaireSnapshotPreviewContract,
} from '@/contracts/questionnaire-snapshot'
import type {
  CustomerApplication,
  ManagedCustomer,
  QuestionnaireAnswer,
} from '@/lib/business-workflow'
import { applicationStatusMeta, customerFilingStatusMeta } from '@/lib/business-workflow'
import { type QuestionType, QuestionTypeSchema } from '@/types/form'

const questionTypeLabels: Record<QuestionType, string> = {
  SHORT_TEXT: '短文字',
  LONG_TEXT: '長文字',
  MULTIPLE_CHOICE: '單選題',
  DROPDOWN: '下拉選單',
  CHECKBOXES: '多選題',
  PICTURE_CHOICE: '圖片選擇',
  YES_NO: '是非題',
  OPINION_SCALE: '意見量表',
  RATING: '評分題',
  NPS: 'NPS 推薦值',
  DATE: '日期',
  TIME: '時間',
  PHONE_NUMBER: '電話號碼',
  TAIWAN_MOBILE: '台灣手機',
  TAIWAN_LANDLINE: '台灣市話',
  TAIWAN_ADDRESS: '台灣地址',
  EMAIL: '電子郵件',
  WEBSITE: '網站連結',
  NUMBER: '數字欄位',
  NUMBER_SLIDER: '數字滑桿',
  TOTAL: '加總計算',
  NESTED_SELECT: '多層選單',
  FILE_UPLOAD: '檔案上傳',
  SIGNATURE: '簽名',
  RANKING: '排序題',
  MATRIX: '矩陣題',
  STATEMENT: '說明文字',
  SECTION_TITLE: '區段標題',
  INTRO_BLOCK: '說明區塊',
  DIVIDER: '分隔線',
}

const sourceStateLabels: Record<CustomerApplicationFieldSourceState, string> = {
  prefilled_from_question: '問卷帶入',
  overridden: '已覆寫',
  manual: '手動輸入',
  missing_source: '未帶入',
}

export const questionnaireSnapshotFormats: QuestionnaireSnapshotFormatOption[] = [
  {
    id: 'html_preview',
    label: 'HTML 預覽',
    enabled: true,
    note: '目前只在前端顯示資料快照，不產生檔案。',
  },
  {
    id: 'json_preview',
    label: 'JSON contract 預覽',
    enabled: true,
    note: '供後續 API / DB / audit 欄位審查使用。',
  },
  {
    id: 'pdf_future',
    label: 'PDF（未接線）',
    enabled: false,
    note: '需後端產生器、儲存策略與 audit log 審查後才能啟用。',
  },
  {
    id: 'excel_future',
    label: 'Excel（未接線）',
    enabled: false,
    note: '需 export job queue 與權限檢查後才能啟用。',
  },
]

export function formatQuestionTypeForSnapshot(questionType: string) {
  const parsed = QuestionTypeSchema.safeParse(questionType)
  if (parsed.success) return questionTypeLabels[parsed.data]
  return /^[A-Z0-9_]+$/.test(questionType) ? '其他題型' : questionType
}

export function buildCustomerQuestionnaireSnapshot({
  customer,
  sourceApplication,
  sourceLink,
  fieldValues,
}: {
  customer: ManagedCustomer
  sourceApplication?: CustomerApplication | null
  sourceLink?: CustomerApplicationSourceLinkContract
  fieldValues?: CustomerApplicationFieldValueContract[]
}): QuestionnaireSnapshotPreviewContract {
  const metadata: QuestionnaireSnapshotMetadata[] = [
    { label: '問卷標題', value: customer.formTitle },
    { label: '來源類型', value: sourceLink ? '問卷記錄建立' : '手動建立' },
    { label: '客戶狀態', value: customer.status === 'active' ? '啟用' : '已歸檔' },
    { label: 'ERP 建檔狀態', value: customerFilingStatusMeta[customer.filingStatus].label },
    { label: '負責業務', value: `${customer.ownerName} · ${customer.ownerEmail}` },
    { label: '主管', value: `${customer.supervisorName} · ${customer.teamName}` },
  ]

  if (sourceApplication) {
    metadata.push(
      { label: '申請狀態', value: applicationStatusMeta[sourceApplication.status].label },
      { label: '送出時間', value: sourceApplication.submittedAt },
    )
  }

  if (sourceLink) {
    metadata.push(
      { label: '來源記錄', value: sourceLink.sourceRecordId },
      { label: 'Response ID', value: sourceLink.responseId },
    )
  }

  const answers = mapAnswers(customer.answers)
  const fields = fieldValues?.length ? mapFieldValues(fieldValues) : mapCustomerFields(customer)

  return {
    id: `snapshot-${customer.id}`,
    title: `${customer.companyName} 問卷資料快照`,
    description:
      '這是 frontend-only mock snapshot preview，用來確認未來單筆問卷資料下載所需欄位、權限與 audit 語意。',
    surface: 'customer_application_detail',
    recommendedSurface: 'customer_application_detail',
    formId: customer.formId,
    formTitle: customer.formTitle,
    responseId: sourceLink?.responseId,
    sourceRecordId: sourceLink?.sourceRecordId,
    customerName: customer.companyName,
    submittedAt: sourceApplication?.submittedAt ?? customer.createdAt,
    generatedAt: new Date().toISOString(),
    providerState: 'mock_snapshot',
    permissionRequired: 'customers:snapshot:read',
    auditRequirement:
      'production 產生或下載單筆資料快照必須寫入 audit_logs，包含 actor、customer、response 與格式。',
    warning: '目前只顯示 mock-safe 資料快照，不產生真 PDF、Excel、CSV 或外部下載連結。',
    formats: questionnaireSnapshotFormats,
    metadata,
    fields,
    answers,
    sourceMeta: createSyntheticMeta('2026-05-22'),
  }
}

function mapAnswers(answers: QuestionnaireAnswer[]): QuestionnaireSnapshotAnswer[] {
  return answers.map((answer) => ({
    id: answer.id,
    questionTitle: answer.questionTitle,
    questionTypeLabel: formatQuestionTypeForSnapshot(answer.questionType),
    answer: answer.answer || '未填寫',
    mappedFieldLabel: getTargetFieldLabel(answer.mappedField),
    sourceStateLabel: '問卷帶入',
  }))
}

function mapFieldValues(
  fieldValues: CustomerApplicationFieldValueContract[],
): QuestionnaireSnapshotField[] {
  return fieldValues.map((fieldValue) => ({
    id: fieldValue.fieldKey,
    label: fieldValue.label,
    value: fieldValue.value || '未填寫',
    sourceStateLabel: sourceStateLabels[fieldValue.sourceState],
    sourceQuestionTitle: fieldValue.sourceQuestionTitle,
  }))
}

function mapCustomerFields(customer: ManagedCustomer): QuestionnaireSnapshotField[] {
  return [
    {
      id: 'companyName',
      label: '公司名稱',
      value: customer.companyName,
      sourceStateLabel: '問卷帶入',
    },
    { id: 'taxId', label: '統編 / Tax ID', value: customer.taxId, sourceStateLabel: '問卷帶入' },
    { id: 'industry', label: '產業', value: customer.industry, sourceStateLabel: '問卷帶入' },
    {
      id: 'contactName',
      label: '聯絡人',
      value: customer.contactName,
      sourceStateLabel: '問卷帶入',
    },
    {
      id: 'contactEmail',
      label: 'Email',
      value: customer.contactEmail,
      sourceStateLabel: '問卷帶入',
    },
    {
      id: 'expectedValue',
      label: '預估成交金額',
      value: customer.expectedValue ? customer.expectedValue.toLocaleString('zh-TW') : '未填寫',
      sourceStateLabel: '問卷帶入',
    },
  ]
}

function getTargetFieldLabel(fieldKey: string) {
  return customerFieldTargetOptions.find((target) => target.value === fieldKey)?.label ?? fieldKey
}
